import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { HiCalendarDays } from "react-icons/hi2";
import IconButton from "components/button/IconButton";
import { FiRefreshCw } from "react-icons/fi";
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} from "reduxFiles/api/authApi";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { useAppDispatch } from "reduxFiles/hooks";
import { HiOutlineSave, HiOutlineTrendingUp } from "react-icons/hi";
import moment from "moment-jalaali";
import Button from "components/button/Button";

const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const { data, refetch, isLoading } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] =
    useUpdateSettingsMutation();

  const [inflationRates, setInflationRates] = useState<{
    currentYear: number;
    lastYear: number;
    twoYearsAgo: number;
  }>({
    currentYear: 0,
    lastYear: 0,
    twoYearsAgo: 0,
  });

  const [limitUpdatedDay, setLimitUpdatedDay] = useState<number>(0);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  useEffect(() => {
    if (data?.settingsJson) {
      const inflation = data.settingsJson.inflation || [0, 0, 0];
      setInflationRates({
        currentYear: inflation[0] || 0,
        lastYear: inflation[1] || 0,
        twoYearsAgo: inflation[2] || 0,
      });

      setLimitUpdatedDay(data.settingsJson.limitUpdatedDay || 0);
      setIsDataLoaded(true);
    } else if (data) {
      setInflationRates({
        currentYear: 0,
        lastYear: 0,
        twoYearsAgo: 0,
      });
      setLimitUpdatedDay(0);
      setIsDataLoaded(true);
    }
  }, [data]);

  const handleSave = async () => {
    if (
      inflationRates.currentYear < 0 ||
      inflationRates.currentYear > 100 ||
      inflationRates.lastYear < 0 ||
      inflationRates.lastYear > 100 ||
      inflationRates.twoYearsAgo < 0 ||
      inflationRates.twoYearsAgo > 100
    ) {
      dispatch(
        addNotification({
          type: "error",
          message: t("settings.invalidInflation"),
        })
      );
      return;
    }

    if (limitUpdatedDay < 0 || limitUpdatedDay > 31) {
      dispatch(
        addNotification({
          type: "error",
          message: t("settings.invalidLimitDay"),
        })
      );
      return;
    }

    try {
      await updateSettings({
        inflation: [
          inflationRates.currentYear,
          inflationRates.lastYear,
          inflationRates.twoYearsAgo,
        ],
        limitUpdatedDay: limitUpdatedDay,
      }).unwrap();

      await refetch();
    } catch (err) {
      console.error(err);
    }
  };

  const handleInflationChange = (
    field: "currentYear" | "lastYear" | "twoYearsAgo",
    value: string
  ) => {
    const numValue = parseFloat(value);
    const validValue = isNaN(numValue)
      ? 0
      : Math.min(Math.max(numValue, 0), 100);

    setInflationRates((prev) => ({
      ...prev,
      [field]: validValue,
    }));
  };

  const getCurrentYear = () => {
    const lang = i18n.language;
    const now = new Date();
    const lastYear = new Date(
      now.getFullYear() - 1,
      now.getMonth(),
      now.getDate()
    );

    if (lang === "fa") {
      return moment(lastYear).format("jYYYY");
    } else {
      return lastYear.getFullYear().toString();
    }
  };

  const getLastYear = () => {
    const lang = i18n.language;
    const now = new Date();
    const lastYear = new Date(
      now.getFullYear() - 2,
      now.getMonth(),
      now.getDate()
    );

    if (lang === "fa") {
      return moment(lastYear).format("jYYYY");
    } else {
      return lastYear.getFullYear().toString();
    }
  };

  const getTwoYearsAgo = () => {
    const lang = i18n.language;
    const now = new Date();
    const twoYearsAgo = new Date(
      now.getFullYear() - 3,
      now.getMonth(),
      now.getDate()
    );

    if (lang === "fa") {
      return moment(twoYearsAgo).format("jYYYY");
    } else {
      return twoYearsAgo.getFullYear().toString();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const lang = i18n.language;
    return moment(dateString).format(
      lang === "fa" ? "jYYYY/jMM/jDD" : "YYYY/MM/DD"
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 lg:p-6"
    >
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-transparent bg-gradient-to-r text-2xl font-bold text-gray-900 dark:text-gray-300">
              {t("settings.pageTitle")}
            </h1>
            <IconButton
              handler={refetch}
              title={t("buttons.refresh")}
              icon={<FiRefreshCw />}
              className="rounded-full bg-white shadow-md dark:bg-gray-800"
            />
          </div>
          {isDataLoaded && data && !data.settingsJson && (
            <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
              {t("settings.notConfigured")}
            </span>
          )}
        </div>

        <div className="relative overflow-hidden rounded-3xl bg-white shadow-xl dark:bg-gray-800/90">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10" />
          <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />

          <div className="relative p-6 lg:p-8">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="loader w-6 text-blue-700" />
              </div>
            ) : (
              <div className="space-y-8">
                {isDataLoaded && data && !data.settingsJson && (
                  <div className="rounded-lg bg-blue-50 p-4 text-sm text-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
                    <p className="flex items-center gap-2">
                      <span className="text-lg">ℹ️</span>
                      {t("settings.firstTimeConfig")}
                    </p>
                  </div>
                )}

                <div>
                  <div className="mb-4 flex items-center gap-3 border-b border-gray-200 pb-4 dark:border-gray-700">
                    <div className="rounded-lg bg-orange-100 p-2 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400">
                      <HiOutlineTrendingUp className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-800 dark:text-white">
                        {t("settings.inflationSettings")}
                      </h2>
                      <p className="text-xs text-gray-700 dark:text-gray-400">
                        {t("settings.inflationDescription")}
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-xl bg-gradient-to-br from-green-50 to-green-50 p-4 dark:from-green-900/20 dark:to-green-900/20">
                      <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {getCurrentYear()}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={inflationRates.currentYear || ""}
                          onChange={(e) =>
                            handleInflationChange("currentYear", e.target.value)
                          }
                          className="w-full rounded-lg border border-green-200 bg-white px-3 py-2 text-center text-lg font-semibold text-gray-800 transition-all focus:border-green-400 focus:outline-none focus:ring-2 focus:ring-green-400/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          placeholder="0"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                          %
                        </span>
                      </div>
                    </div>

                    <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 p-4 dark:from-blue-900/20 dark:to-indigo-900/20">
                      <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {getLastYear()}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={inflationRates.lastYear || ""}
                          onChange={(e) =>
                            handleInflationChange("lastYear", e.target.value)
                          }
                          className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-center text-lg font-semibold text-gray-800 transition-all focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          placeholder="0"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                          %
                        </span>
                      </div>
                    </div>

                    <div className="rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 p-4 dark:from-purple-900/20 dark:to-pink-900/20">
                      <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {getTwoYearsAgo()}
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          step="0.1"
                          value={inflationRates.twoYearsAgo || ""}
                          onChange={(e) =>
                            handleInflationChange("twoYearsAgo", e.target.value)
                          }
                          className="w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-center text-lg font-semibold text-gray-800 transition-all focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                          placeholder="0"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="mb-4 flex items-center gap-3 border-b border-gray-200 pb-4 dark:border-gray-700">
                    <div className="rounded-lg bg-cyan-100 p-2 text-cyan-600 dark:bg-cyan-500/20 dark:text-orange-400">
                      <HiCalendarDays className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-800 dark:text-white">
                        {t("settings.limitDayForUpdatedAssessment")}
                      </h2>
                      <p className="text-xs text-gray-700 dark:text-gray-400">
                        {t("settings.limitDayForUpdatedAssessmentDesc")}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl bg-gradient-to-br from-navy-50 to-navy-50 p-4 dark:from-navy-900/20 dark:to-navy-900/20 md:w-1/2 lg:w-1/5">
                    <div className="relative">
                      <input
                        type="number"
                        step={1}
                        min={0}
                        max={31}
                        value={limitUpdatedDay || ""}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value)) {
                            setLimitUpdatedDay(
                              Math.min(Math.max(value, 0), 31)
                            );
                          } else {
                            setLimitUpdatedDay(0);
                          }
                        }}
                        className="w-full rounded-lg border border-navy-200 bg-white px-3 py-2 text-center text-lg font-semibold text-gray-800 transition-all focus:border-navy-400 focus:outline-none focus:ring-2 focus:ring-navy-400/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                        placeholder="0"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-700">
                        {t("settings.day")}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-700 dark:text-gray-400">
                      {t("settings.limitDayRange")} (0-31)
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4 pt-4">
                  <Button
                    variant="info"
                    onClick={handleSave}
                    disabled={isUpdating}
                    loading={isUpdating}
                    startIcon={<HiOutlineSave size={16} />}
                  >
                    {data?.settingsJson
                      ? t("settings.saveAllSettings")
                      : t("settings.createAndSave")}
                  </Button>

                  {data && !data.settingsJson && (
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      {t("settings.createNewSettings")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 text-center text-sm text-gray-400 dark:text-gray-500">
          {t("settings.lastUpdate")} :{" "}
          {data?.createdAt ? formatDate(data.createdAt) : "-"}
        </div>
      </div>
    </motion.div>
  );
};

export default SettingsPage;
